import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { keepPreviousData } from '@tanstack/react-query';
import Header from '@/components/Header/Header';
import Input from '@/components/core/Input';
import { CenteredColumn } from '@/components/CenteredColumn';
import { DocumentHead } from '@/components/core/DocumentHead';
import { Loading } from '@/components/Loading';
import { Spinner } from '@/components/core/Spinner';
import { IconSearch, IconXmark } from '@/icons';
import {
  useListOrganizations,
  useTravellerGlobalSearch,
} from '@/api/trafikportalen';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { toCardSearchQuery } from '@/helpers/cardSerial';
import {
  buildTravellerSearchHighlights,
  groupTravellerSearchResults,
} from '@/helpers/travellerSearch';
import { useImpersonation } from '@/hooks/useImpersonation';
import { PurgeCardPanel } from '@/components/Search/PurgeCardPanel';
import { TravellerSearchResultGroup } from '@/components/Search/TravellerSearchResult';
import { useTravellerSearchStore } from '@/stores/travellerSearch';
import IconButton from '@/components/core/IconButton';

interface SearchParams {
  q?: string;
}

export const Route = createFileRoute('/$tenantId/_layout/search/')({
  component: Search,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    q: typeof search.q === 'string' ? search.q : undefined,
  }),
  beforeLoad: ({ params }) => {
    if (!checkUserPermissions({ permission: 'tenant:viewer' })) {
      throw redirect({ to: '/$tenantId', params });
    }
  },
});

function Search() {
  const { formatMessage } = useIntl();
  const { q } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const setStoredQuery = useTravellerSearchStore((state) => state.setQuery);

  const [query, setQuery] = useState(
    () => q ?? useTravellerSearchStore.getState().query,
  );

  // Index of the keyboard-highlighted result and, when a person exists in
  // several organizations, the highlighted organization within that result.
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedOrgIndex, setSelectedOrgIndex] = useState(0);

  const trimmedQuery = query.trim();
  const cardSearchQuery = toCardSearchQuery(query);
  const searchQuery = cardSearchQuery ?? trimmedQuery;
  const enabled = searchQuery.length >= 3;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (query !== (q ?? '')) {
      navigate({
        search: { q: query || undefined },
        replace: true,
      });
    }
  }, [query, q, navigate]);

  useEffect(() => {
    setStoredQuery(query);
  }, [query, setStoredQuery]);

  const {
    data: results,
    isFetching,
    isError,
  } = useTravellerGlobalSearch(
    { q: searchQuery },
    { query: { enabled, placeholderData: keepPreviousData } },
  );

  const { data: organizations } = useListOrganizations();

  const impersonate = useImpersonation();

  const organizationNameById = useMemo(() => {
    const map = new Map<string, string>();
    organizations?.forEach((organization) => {
      map.set(organization.id, organization.name);
    });
    return map;
  }, [organizations]);

  const groups = useMemo(
    () => (results ? groupTravellerSearchResults(results) : []),
    [results],
  );

  const sortedGroups = useMemo(
    () =>
      groups.map((group) => ({
        ...group,
        records: [...group.records].sort((a, b) => {
          // A deleted traveller cannot be opened, so openable records come first.
          const byDeleted =
            Number(Boolean(a.traveller.deleted_at)) -
            Number(Boolean(b.traveller.deleted_at));
          if (byDeleted !== 0) {
            return byDeleted;
          }

          return (
            organizationNameById.get(a.traveller.organization_id) ?? ''
          ).localeCompare(
            organizationNameById.get(b.traveller.organization_id) ?? '',
          );
        }),
      })),
    [groups, organizationNameById],
  );

  const highlights = useMemo(
    () =>
      results
        ? buildTravellerSearchHighlights(results, searchQuery)
        : undefined,
    [results, searchQuery],
  );

  const handleImpersonate = useCallback(
    (organizationId: string, travellerId: string) => {
      impersonate(organizationId, '/$tenantId/travellers/$travellerId/$view', {
        travellerId,
        view: 'profile',
      });
    },
    [impersonate],
  );

  const itemCount = sortedGroups.length;
  const activeGroup =
    selectedIndex >= 0 ? sortedGroups[selectedIndex] : undefined;
  const orgCount = activeGroup?.records.length ?? 0;

  const handleClear = useCallback(() => {
    setQuery('');
    setSelectedIndex(-1);
    setSelectedOrgIndex(0);
    inputRef.current?.focus();
  }, []);

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedText = event.clipboardData.getData('text');
      const withoutSpaces = pastedText.replace(/\s/g, '');

      if (withoutSpaces === pastedText || !/^\d+$/.test(withoutSpaces)) {
        return;
      }

      event.preventDefault();

      const input = event.currentTarget;
      const start = input.selectionStart ?? query.length;
      const end = input.selectionEnd ?? query.length;
      setQuery(`${query.slice(0, start)}${withoutSpaces}${query.slice(end)}`);

      const cursor = start + withoutSpaces.length;
      queueMicrotask(() => {
        input.setSelectionRange(cursor, cursor);
      });
    },
    [query],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!enabled || itemCount === 0) {
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev < 0 ? 0 : Math.min(prev + 1, itemCount - 1),
          );
          setSelectedOrgIndex(0);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => (prev <= 0 ? 0 : prev - 1));
          setSelectedOrgIndex(0);
          break;
        // Only hijack left/right when there are multiple organizations to move
        // between, so they keep editing the query text otherwise.
        case 'ArrowLeft':
          if (orgCount > 1) {
            event.preventDefault();
            setSelectedOrgIndex((prev) => Math.max(prev - 1, 0));
          }
          break;
        case 'ArrowRight':
          if (orgCount > 1) {
            event.preventDefault();
            setSelectedOrgIndex((prev) => Math.min(prev + 1, orgCount - 1));
          }
          break;
        case 'Enter': {
          if (selectedIndex < 0) {
            return;
          }
          event.preventDefault();
          const record = activeGroup?.records[selectedOrgIndex];
          if (record && !record.traveller.deleted_at) {
            handleImpersonate(
              record.traveller.organization_id,
              record.traveller.id,
            );
          }
          break;
        }
        case 'Escape':
          if (selectedIndex >= 0) {
            event.preventDefault();
            setSelectedIndex(-1);
            setSelectedOrgIndex(0);
          }
          break;
        case 'Tab':
          setSelectedIndex(-1);
          setSelectedOrgIndex(0);
          break;
      }
    },
    [
      enabled,
      itemCount,
      orgCount,
      selectedIndex,
      selectedOrgIndex,
      activeGroup,
      handleImpersonate,
    ],
  );

  useEffect(() => {
    setSelectedIndex(0);
    setSelectedOrgIndex(0);
  }, [trimmedQuery]);

  useEffect(() => {
    if (selectedIndex < 0) {
      return;
    }
    listRef.current?.children[selectedIndex]?.scrollIntoView({
      block: 'nearest',
    });
  }, [selectedIndex]);

  const announcement = useMemo(() => {
    if (!enabled) return '';
    if (isError) {
      return formatMessage({
        defaultMessage: 'Ett fel uppstod vid sökningen. Försök igen.',
      });
    }
    if (!results) return '';
    return formatMessage(
      {
        defaultMessage:
          '{count, plural, =0 {Inga resenärer hittades} one {En resenär hittades} other {# resenärer hittades}}',
      },
      { count: itemCount },
    );
  }, [enabled, isError, results, itemCount, formatMessage]);

  return (
    <div className="flex grow flex-col overflow-hidden">
      <DocumentHead
        titleKey="page.search.title"
        descriptionKey="page.search.description"
      />
      <Header title={formatMessage({ defaultMessage: 'Sök' })} />
      <div className="flex grow flex-col overflow-y-auto pb-6">
        <CenteredColumn>
          <div role="search" className="flex gap-1">
            <Input
              ref={inputRef}
              type="text"
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              placeholder={formatMessage({
                defaultMessage:
                  'Sök på namn, e-post, personnummer, telefon eller kortnummer',
              })}
              startAdornment={<IconSearch aria-hidden="true" />}
              endAdornment={
                query || isFetching ? (
                  <div className="flex items-center gap-1">
                    {isFetching && <Spinner className="size-5" />}
                  </div>
                ) : undefined
              }
              aria-label={formatMessage({
                defaultMessage: 'Sök efter resenärer',
              })}
            />
            <IconButton
              onClick={handleClear}
              variant="ghost"
              aria-label={formatMessage({
                defaultMessage: 'Rensa sökning',
              })}
              icon={<IconXmark aria-hidden="true" />}
              disabled={!query}
            />
          </div>

          <div aria-live="polite" aria-atomic="true" className="sr-only">
            {announcement}
          </div>

          {!enabled ? (
            <div className="flex flex-col gap-2 text-text-700">
              <p>
                <FormattedMessage defaultMessage="Skriv minst 3 tecken. Visar som mest 10 resultat åt gången. Du kan söka på hela eller delar av ett kortnummer; kortnumret visas bara när det matchar. Borttagna resenärer visas med datum och kan inte öppnas. Använd CTRL+F som genväg för att komma till denna sidan." />
              </p>
              {checkUserPermissions({ permission: 'tenant:editor' }) && (
                <p>
                  <FormattedMessage defaultMessage="När ett resekort matchar kan du välja det och rensa det så serienumret kan användas igen." />
                </p>
              )}
            </div>
          ) : isError ? (
            <p className="text-red-700">
              <FormattedMessage defaultMessage="Ett fel uppstod vid sökningen. Försök igen." />
            </p>
          ) : results && results.length > 0 ? (
            <>
              <PurgeCardPanel results={results} query={searchQuery} />
              <ul
                ref={listRef}
                className="flex flex-col gap-2"
                aria-label={formatMessage({ defaultMessage: 'Sökresultat' })}
              >
                {sortedGroups.map((group, index) => (
                  <li key={group.key}>
                    <TravellerSearchResultGroup
                      group={group}
                      organizationNameById={organizationNameById}
                      onImpersonate={handleImpersonate}
                      highlights={highlights}
                      query={searchQuery}
                      activeOrgIndex={
                        selectedIndex === index ? selectedOrgIndex : -1
                      }
                    />
                  </li>
                ))}
              </ul>
            </>
          ) : isFetching ? (
            <Loading />
          ) : (
            <p className="text-text-700">
              <FormattedMessage
                defaultMessage="Inga matchningar för ”{query}”."
                values={{ query: trimmedQuery }}
              />
            </p>
          )}
        </CenteredColumn>
      </div>
    </div>
  );
}
